<template>
  <div>
    <div class="app-container app-container--with-progress">
      <div class="app-container-route">
        <div class="membership-selected">
          {{ memberDetails.membershipType.value }}
          <span />
        </div>

        <HeaderWithIcon :title="pageTitle" />

        <div v-if="!showUpdateEmail">
          <div class="notice notice--success">
            <p>
              A verification email has been sent to the address displayed below.
              <br />
              Please go to your inbox and click <b>Confirm my Account</b> to
              continue sign up.
            </p>
          </div>

          <div class="email-block">{{ memberDetails.userName.value }}</div>

          <div class="update-email">
            Wrong email address?
            <span @click="showUpdateEmail = true">Click here to update</span>
          </div>

          <hr />

          <div class="resend-verification">
            <p>
              <b>Not recieved an email?</b><br />
              Please check your junk folder and allow up to 10mins.
            </p>
            <a @click="resendVerification" style="text-decoration: underline;">
              Resend verification email
            </a>
          </div>

          <router-link
            v-if="allowSkipVerification"
            class="btn btn--positive"
            :to="{ name: 'CheckVerification' }"
            style="margin-top: 2em;"
          >
            Skip Verification
          </router-link>
        </div>

        <div v-else>
          <div class="notice notice--warn">
            <p>
              <strong>Please note:</strong> updating your email address will log
              you out of the website. <br />
              To continue with the registration process you will need to log
              back in.
            </p>
          </div>

          <ValidationObserver v-slot="{ handleSubmit }">
            <form @submit.prevent="handleSubmit(updateEmail)">
              <div class="form-control__item">
                <ValidationProvider
                  rules="required|email"
                  v-slot="{ errors, invalid, touched }"
                >
                  <div class="form-control-item form-control-item--text">
                    <label for="newEmailLogin">Email Address</label>
                    <input
                      type="email"
                      v-model="newLoginEmail"
                      placeholder="your@email.com"
                      :class="[{ invalid: invalid }, { touched: touched }]"
                    />
                    <span class="validation-label">{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>
              <hr />

              <div class="form-actions">
                <a @click="showUpdateEmail = false">Back</a>
                <input
                  type="submit"
                  value="Update and Send"
                  class="btn btn--positive"
                />
              </div>
            </form>
          </ValidationObserver>
        </div>
      </div>
      <div class="app-container-progress">
        <progress-bar />
      </div>
    </div>
    <spinner />
    <notification />
  </div>
</template>

<script>
import ProgressBar from "@/components/ProgressBar.vue";
import Spinner from "@/components/Spinner";
import Notification from "@/components/Notification";
import { useLoggedInStatus } from "@/helpers/loggedInStatus";
import { useMemberDetails } from "@/helpers/member-details";
import { doPost } from "@/helpers/fetch-helper";
import { useNotification } from "@/helpers/notification";
import { computed, ref, watch } from "@vue/composition-api";
import { useShowSpinner } from "@/helpers/showSpinner";
import { ValidationProvider, ValidationObserver, extend } from "vee-validate";
import { required, email } from "vee-validate/dist/rules";
import HeaderWithIcon from "@/components/HeaderWithIcon";

extend("required", {
  ...required,
  message: "This field is required",
});
extend("email", {
  ...email,
  message: "Please enter a valid email address",
});

export default {
  name: "VerifyAccount",
  components: {
    ValidationObserver,
    ValidationProvider,
    ProgressBar,
    Spinner,
    Notification,
    HeaderWithIcon,
  },
  setup() {
    const { isLoggedIn } = useLoggedInStatus(),
      { setShowSpinner } = useShowSpinner(),
      { showNotification } = useNotification(),
      memberDetails = useMemberDetails(),
      showUpdateEmail = ref(false),
      newLoginEmail = ref(""),
      allowSkipVerification = ref(false);

    if (process.env.NODE_ENV === "development")
      allowSkipVerification.value = true;

    const pageTitle = computed(() => {
      if (!showUpdateEmail.value) {
        return "Confirm Email";
      } else {
        return "Update Email Address";
      }
    });

    watch(() => newLoginEmail);

    return {
      allowSkipVerification,
      isLoggedIn,
      memberDetails,
      showUpdateEmail,
      newLoginEmail,
      setShowSpinner,
      showNotification,
      pageTitle,
    };
  },
  methods: {
    async resendVerification() {
      const response = await doPost("/ResendVerificationEmail");

      if (response.success) {
        this.showNotification(
          "success",
          "A new verification email has been sent",
        );
      } else {
        this.showNotification(
          "error",
          "There was a problem resending your verification email, please contact us",
        );
      }
    },
    async updateEmail() {
      this.setShowSpinner(true);
      const response = await doPost("/UpdateLoginEmail", {
        Email: this.newLoginEmail,
      });

      this.setShowSpinner(false);

      if (response.updated) {
        this.showNotification(
          "success",
          "Email was successfully changed, you will now be logged out",
        );
        window.location.href = `/auth/requestsignin?returnurl=${window.location.pathname}`;
      } else {
        this.showNotification(
          "error",
          "There was a problem updating your email, please contact us",
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/global-variables.scss";

.app-container {
  &-route {
    .email-block {
      background: #ffffff;
      border: 1px solid rgba(114, 114, 114, 0.1);
      border-radius: 3px;
      color: var(--body-light);
      font-size: 18px;
      font-weight: 700;
      line-height: 36px;
      margin-bottom: 16px;
      padding: 16px;
    }

    .update-email {
      color: var(--primary);
      line-height: 28px;
      padding: {
        left: 16px;
        right: 16px;
      }

      span {
        cursor: pointer;
        font-weight: 700;
        text-decoration: underline;
      }
    }

    .resend-verification {
      color: var(--body-light);
      padding: {
        left: 16px;
        right: 16px;
      }

      p {
        line-height: 28px;
        margin-bottom: 32px;
      }

      a {
        color: var(--primary);
        cursor: pointer;
        font-weight: 700;
      }
    }

    .form-actions {
      align-items: center;
      display: flex;
      justify-content: flex-end;

      .btn {
        min-width: 200px;
      }

      a {
        cursor: pointer;
        margin-right: 32px;
      }
    }
  }
}
</style>
